// Shared course pace: pure, dependency-free functions used by the app, the Worker
// (api/src/frontier.ts may re-export these) and tests.
//
// Model (the users' decisions of 2026-10-05, docs/COURSE.md):
// - Lesson n (1-based) is scheduled for the Saturday of week n: `startDate + 7·(n−1)` days, local
//   time in `pace.timezone` (Europe/Helsinki). With lessonsPerWeek = k, k lessons per Saturday.
// - It OPENS on that day only once both users have passed lesson n−1 (`lessonsOpen`). Behind
//   schedule, the next lesson opens as soon as both have passed — never before its own date.
// - New words are released to *both* users at once ("the frontier"): once per local day the
//   frontier advances by newPerDay(today) — 5/day for the first `firstMonthDays`, then the rate
//   that spreads every course word to the last lesson — never past the words of OPEN lessons, and
//   not at all while either user has more than `pauseIfOverdueAbove` overdue reviews. Behind the
//   calendar (`releaseTarget`), a day releases up to twice its rate until it has caught up.
// - A released word becomes available to a user once that user has passed its lesson's check.
//
// Dates: a "local date" is an ISO calendar date string "YYYY-MM-DD" in the pace timezone.
// Functions taking `DateInput` accept either a local date string or an instant (Date or an ISO
// timestamp with a time part), which is converted to the local date in the given timezone.

import type { EarlyOpen, LessonMeta, PaceConfig, PaceRise, PaceStaircase } from './types';

export type DateInput = Date | string;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function dtf(timeZone: string): Intl.DateTimeFormat {
  let f = dtfCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dtfCache.set(timeZone, f);
  }
  return f;
}

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function localParts(instant: Date, timeZone: string): LocalParts {
  const out: Record<string, number> = {};
  for (const p of dtf(timeZone).formatToParts(instant)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year ?? 1970,
    month: out.month ?? 1,
    day: out.day ?? 1,
    hour: (out.hour ?? 0) % 24,
    minute: out.minute ?? 0,
    second: out.second ?? 0,
  };
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** Local calendar date ("YYYY-MM-DD") of an instant in `timeZone`. */
export function localDate(instant: Date | string | number, timeZone = 'Europe/Helsinki'): string {
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) throw new RangeError(`Invalid date: ${String(instant)}`);
  const p = localParts(d, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/** Local wall-clock time "HH:MM" of an instant in `timeZone` (for reminder checks). */
export function localTime(instant: Date | string | number, timeZone = 'Europe/Helsinki'): string {
  const d = instant instanceof Date ? instant : new Date(instant);
  const p = localParts(d, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Offset of `timeZone` from UTC at `instant`, in minutes (Helsinki: +120 or +180). */
export function tzOffsetMinutes(instant: Date, timeZone: string): number {
  const p = localParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** The instant at which local date `date` begins (00:00) in `timeZone`. */
export function startOfLocalDay(date: string, timeZone = 'Europe/Helsinki'): Date {
  assertIsoDate(date);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d);
  let t = guess - tzOffsetMinutes(new Date(guess), timeZone) * 60_000;
  // Second pass handles DST changes between the guess and the result.
  t = guess - tzOffsetMinutes(new Date(t), timeZone) * 60_000;
  return new Date(t);
}

/** Normalise a DateInput to a local date string. */
export function toLocalDate(date: DateInput, timeZone = 'Europe/Helsinki'): string {
  if (typeof date === 'string' && ISO_DATE.test(date)) return date;
  return localDate(date, timeZone);
}

function assertIsoDate(date: string): void {
  if (!ISO_DATE.test(date)) throw new RangeError(`Expected YYYY-MM-DD, got ${date}`);
}

function dateToUtcMs(date: string): number {
  assertIsoDate(date);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function utcMsToDate(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Calendar arithmetic on local dates (DST-safe: works on dates, not instants). */
export function addDays(date: string, n: number): string {
  return utcMsToDate(dateToUtcMs(date) + n * DAY_MS);
}

/** Whole days from `a` to `b` (b − a); negative when b is before a. */
export function daysBetween(a: string, b: string): number {
  return Math.round((dateToUtcMs(b) - dateToUtcMs(a)) / DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: string): number {
  return new Date(dateToUtcMs(date)).getUTCDay();
}

/** ISO week label like "2026-W44" (for the weekly shared session). */
export function isoWeek(date: string): string {
  const ms = dateToUtcMs(date);
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // Monday = 0
  const thursday = ms + (3 - dow) * DAY_MS;
  const ty = new Date(thursday).getUTCFullYear();
  const jan4 = Date.UTC(ty, 0, 4);
  const jan4dow = (new Date(jan4).getUTCDay() + 6) % 7;
  const week1Monday = jan4 - jan4dow * DAY_MS;
  const week = Math.floor((thursday - week1Monday) / (7 * DAY_MS)) + 1;
  return `${ty}-W${pad(week)}`;
}

// ---------- Pace ----------

/** Lessons open on Saturdays (0 = Sunday … 6 = Saturday): pace.startDate is one. */
export const LESSON_WEEKDAY = 6;

/**
 * The day on the course timetable (0 on the start date; negative before): days since lesson 1 was
 * scheduled, plus the days early openings have moved the timetable forward by `date`
 * (scheduleShift). The schedule, the word rate and the release target all count on it, so after an
 * early opening the whole course runs that much earlier (Axel, 2026-10-07).
 */
export function courseDay(date: DateInput, pace: PaceConfig): number {
  const d = toLocalDate(date, pace.timezone);
  return daysBetween(pace.startDate, d) + scheduleShift(d, pace);
}

/**
 * Highest lesson order whose calendar date has come by `date` (0 before the start date): the
 * schedule. A lesson actually opens only once both have passed the one before (`lessonsOpen`).
 */
export function lessonsUnlocked(date: DateInput, pace: PaceConfig): number {
  const day = courseDay(date, pace);
  if (day < 0) return pace.firstLessonDate && toLocalDate(date, pace.timezone) >= pace.firstLessonDate ? 1 : 0;
  return (Math.floor(day / 7) + 1) * Math.max(1, pace.lessonsPerWeek);
}

/** The schedule, capped at the course's last lesson. */
export function lessonsScheduled(date: DateInput, pace: PaceConfig, lessonCount = Infinity): number {
  return Math.min(lessonsUnlocked(date, pace), Math.max(0, lessonCount));
}

/**
 * Local date on which lesson `order` is scheduled to open: a Saturday, or — after an early opening
 * (earlyOpensOf) — the weekday that opening fell on, a week apart from it.
 */
export function unlockDateOf(order: number, pace: PaceConfig): string {
  if (order <= 1 && pace.firstLessonDate && pace.firstLessonDate < pace.startDate) return pace.firstLessonDate;
  return dateUnder(earlyOpensOf(pace), order, pace);
}

/** Next scheduled date strictly after `date` (the start date itself before the course begins). */
export function nextUnlock(date: DateInput, pace: PaceConfig): string {
  if (courseDay(date, pace) < 0) return pace.startDate;
  return unlockDateOf(lessonsUnlocked(date, pace) + 1, pace);
}

/** Highest k such that lessons 1…k are all in `passedOrders` (0 when lesson 1 is not). */
export function passedThrough(passedOrders: Iterable<number>): number {
  const set = new Set(passedOrders);
  let k = 0;
  while (set.has(k + 1)) k++;
  return k;
}

/** What both have passed: the smallest passedThrough of every user (0 for no users). */
export function passedByAll(perUser: Record<string, number>): number {
  const xs = Object.values(perUser);
  return xs.length ? Math.max(0, Math.min(...xs)) : 0;
}

/**
 * Lessons open for both on `date`: lesson n opens on its scheduled date once both have passed
 * lesson n − 1 (`bothPassedThrough` = passedByAll). Lesson 1 opens on the start date. With k
 * lessons a week, a week's k lessons open together once the last lesson before them is passed.
 */
export function lessonsOpen(date: DateInput, pace: PaceConfig, bothPassedThrough: number, lessonCount = Infinity): number {
  const scheduled = lessonsScheduled(date, pace, lessonCount);
  if (scheduled <= 0) return 0;
  const k = Math.max(1, pace.lessonsPerWeek);
  const gate = Math.max(0, Math.floor(bothPassedThrough));
  return Math.min(scheduled, (Math.floor(gate / k) + 1) * k);
}

export interface ScheduleStatus {
  /** Lessons whose date has come (capped at the course length). */
  scheduled: number;
  open: number;
  /** Lessons behind schedule: scheduled but not open yet (0 = on schedule). */
  behind: number;
  /** The next lesson's order (null after the last one). */
  next: number | null;
  /** Its scheduled date; it opens then if both have passed `open` by then. */
  nextDate: string | null;
  /** The next lesson's date has come: it opens as soon as both have passed lesson `open`. */
  nextWaitsForPasses: boolean;
  lastLessonDate: string | null;
  /** pace.goalDate (Halloween 2027), if set. */
  goalDate: string | null;
}

/** Where the two of you are against the calendar. */
export function scheduleStatus(date: DateInput, pace: PaceConfig, open: number, lessonCount: number): ScheduleStatus {
  const today = toLocalDate(date, pace.timezone);
  const scheduled = lessonsScheduled(today, pace, lessonCount);
  const next = open < lessonCount ? open + 1 : null;
  const nextDate = next ? unlockDateOf(next, pace) : null;
  return {
    scheduled,
    open,
    behind: Math.max(0, scheduled - open),
    next,
    nextDate,
    nextWaitsForPasses: !!nextDate && nextDate <= today && open > 0,
    lastLessonDate: lessonCount > 0 ? unlockDateOf(lessonCount, pace) : null,
    goalDate: pace.goalDate ?? null,
  };
}

/**
 * New words released per day on `date` (0 before the start date): the first month's rate, then the
 * staircase's stair (pace.staircase; never past the stairs reached, pace.rises) or the old flat
 * newPerDayAfter.
 */
export function newPerDay(date: DateInput, pace: PaceConfig): number {
  return rateOnCourseDay(courseDay(date, pace), pace, riseDays(pace));
}

/**
 * Words the plan has released by the end of `date`: newPerDay summed from the start. With the
 * stairs reached (pace.rises) it follows them, so a held stair lowers the target too.
 */
export function releaseTarget(date: DateInput, pace: PaceConfig): number {
  return targetOnCourseDay(courseDay(date, pace), pace);
}

/** newPerDay summed over course days 0…`day` (0 before the start). */
function targetOnCourseDay(day: number, pace: PaceConfig): number {
  if (day < 0) return 0;
  const first = Math.min(day + 1, pace.firstMonthDays);
  const s = pace.staircase;
  if (s?.hold) {
    const reached = riseDays(pace);
    let n = 0;
    for (let d = 0; d <= day; d++) n += rateOnCourseDay(d, pace, reached);
    return n;
  }
  if (!s) return first * pace.newPerDayFirstMonth + Math.max(0, day + 1 - pace.firstMonthDays) * pace.newPerDayAfter;
  const reached = riseDays(pace);
  let n = first * pace.newPerDayFirstMonth;
  for (let d = pace.firstMonthDays; d <= day; d++) n += stairRate(stairOn(d, s, pace, reached), s);
  return n;
}

// ---------- The rising word pace (Axel, 2026-10-14; docs/COURSE.md "Pacing") ----------
//
// After the first month the rate climbs an even staircase (pace.staircase): `start` a day (6), `step`
// more (1) at each of `steps` even intervals up to `until`, the last words' day (7 stairs of ~46 days,
// 6 → 12, with 3025 course words; the build computes `steps` and `until`: scripts/src/lib/course-plan.ts
// ratePlan). Stair k begins on course day firstMonthDays + round(k · span / steps).
//
// A stair goes up only when it is going well (riseGate): both of you have kept your recall (the share
// of review-state card reviews answered right over the last two weeks ≥ staircase.minRecall) and
// neither is past the overdue pause. The server checks once a day when a stair is due
// (api/src/frontier.ts ensureFrontier → checkRise) and records the stairs reached (pace.rises): the
// rate and the release target follow them, so a held stair holds the rate and the buffer absorbs it
// (no "behind target" catch-up after a hold). After a rise the next one waits at least
// STAIRCASE_RULES.minDaysBetweenRises, so a late staircase catches up one stair a week at most.
// The first month's end (5 → 6) is the plan's start, not a rise: it is never held.

/** The staircase's rules, as data (docs/design/SANAPAJA.md "Rising word pace", Axel 2026-10-14). */
export const STAIRCASE_RULES = {
  /** Recall is measured over the last this many days ("the last two weeks"). */
  recallDays: 14,
  /**
   * Fewer review-state card reviews than this in the window and recall is not known: the rise waits
   * (a rise needs it to be going well; two weeks away from the cards is not that).
   */
  minRecallReviews: 30,
  /** After a rise the next one waits at least this many days (a held staircase catches up a stair a week). */
  minDaysBetweenRises: 7,
} as const;

const stairCount = (s: PaceStaircase) => Math.max(1, Math.floor(s.steps));

// ---- The initial hold (Axel 2026-10-25; PaceStaircase.hold): at most hold.perDay new words a day up to hold.until,
// the stairs from the day after it. Rules as data: the hold lives in the pace (engine/src/pace-defaults.ts).

/** The first course day after the hold (0 = no hold). */
function holdEndDay(pace: PaceConfig): number {
  const h = pace.staircase?.hold;
  return h && ISO_DATE.test(h.until) ? Math.max(0, daysBetween(pace.startDate, h.until) + 1) : 0;
}

/** The course day the stairs begin: the first month's end or the day after the hold, whichever is later. */
function stairBegin(pace: PaceConfig): number {
  return Math.max(pace.firstMonthDays, holdEndDay(pace));
}

/** Is course day `day` (of local date `date`) inside the hold? */
export function inPaceHold(date: DateInput, pace: PaceConfig): boolean {
  const d = courseDay(date, pace);
  return d >= 0 && d < holdEndDay(pace);
}
const stairRate = (k: number, s: PaceStaircase) => Math.max(0, s.start + k * s.step);

/** The staircase's days: course days firstMonthDays … the course day of `until` (at least 1). */
function stairSpan(s: PaceStaircase, pace: PaceConfig): number {
  return Math.max(1, daysBetween(pace.startDate, s.until) + 1 - stairBegin(pace));
}

/** The course day the calendar puts stair `k` on (k = 0 … steps − 1): the span split evenly. */
function stairStartDay(k: number, s: PaceStaircase, pace: PaceConfig): number {
  return stairBegin(pace) + Math.round((k * stairSpan(s, pace)) / stairCount(s));
}

/** The calendar's stair on course day `day` (≥ firstMonthDays). */
function calendarStair(day: number, s: PaceStaircase, pace: PaceConfig): number {
  const n = stairCount(s);
  let k = 0;
  while (k + 1 < n && stairStartDay(k + 1, s, pace) <= day) k++;
  return k;
}

/**
 * The course days the stairs reached began on (index i = stair i + 1), from pace.rises: null when the
 * pace carries none (the calendar alone). Only an unbroken run 1, 2, 3 … counts.
 */
function riseDays(pace: PaceConfig): number[] | null {
  if (!pace.rises) return null;
  const out: number[] = [];
  const sorted = pace.rises.filter((r) => Number.isInteger(r.step) && r.step >= 1 && ISO_DATE.test(r.date)).sort((a, b) => a.step - b.step);
  for (const r of sorted) {
    if (r.step <= out.length) continue;
    if (r.step > out.length + 1) break;
    out.push(Math.max(courseDay(r.date, pace), out.at(-1) ?? -Infinity));
  }
  return out;
}

/** The stair the rate is on, course day `day`: the calendar's, never past the stairs reached. */
function stairOn(day: number, s: PaceStaircase, pace: PaceConfig, reached: readonly number[] | null): number {
  const cal = calendarStair(day, s, pace);
  if (!reached) return cal;
  let k = 0;
  while (k < reached.length && reached[k]! <= day) k++;
  return Math.min(cal, k);
}

function rateOnCourseDay(day: number, pace: PaceConfig, reached: readonly number[] | null): number {
  if (day < 0) return 0;
  const h = pace.staircase?.hold;
  if (h && day < holdEndDay(pace)) return Math.max(0, Math.min(h.perDay, day < pace.firstMonthDays ? pace.newPerDayFirstMonth : stairRate(0, pace.staircase!)));
  if (day < pace.firstMonthDays) return pace.newPerDayFirstMonth;
  const s = pace.staircase;
  return s ? stairRate(stairOn(day, s, pace, reached), s) : pace.newPerDayAfter;
}

/** The first local date on the course timetable whose course day is ≥ `day` (early openings move it earlier). */
function dateOfCourseDay(day: number, pace: PaceConfig): string {
  const opens = earlyOpensOf(pace);
  const maxShift = opens.length ? scheduleShift(opens.at(-1)!.date, pace) : 0;
  let d = addDays(pace.startDate, day - maxShift);
  while (courseDay(d, pace) < day) d = addDays(d, 1);
  return d;
}

/**
 * Planned start (course day) of every stair, given the stairs reached and today (course day `t`):
 * a reached stair began when it was reached; the next one comes on its calendar day, at least
 * minDaysBetweenRises after the last rise; the ones after it as if it went up no earlier than today.
 */
function stairStarts(s: PaceStaircase, pace: PaceConfig, reached: readonly number[] | null, t: number): number[] {
  const n = stairCount(s);
  const out: number[] = [stairStartDay(0, s, pace)];
  for (let k = 1; k < n; k++) {
    const cal = stairStartDay(k, s, pace);
    if (!reached) {
      out.push(cal);
      continue;
    }
    if (k <= reached.length) {
      out.push(reached[k - 1]!);
      continue;
    }
    const prev = out[k - 1]!;
    const wait = k >= 2 ? prev + STAIRCASE_RULES.minDaysBetweenRises : prev;
    const planned = Math.max(cal, wait);
    // the stairs after the next one: as if the next went up no earlier than today
    out.push(k === reached.length + 1 ? planned : Math.max(planned, t + (k - reached.length - 1) * STAIRCASE_RULES.minDaysBetweenRises));
  }
  return out;
}

/** One row of the step table (paceSteps). */
export interface PaceStep {
  /** -1 = the first month; 0 … steps − 1 = the stairs (the flat pace: 0 = newPerDayAfter). */
  step: number;
  /** New words a day on it. */
  rate: number;
  /** Its first local date: when it was reached, or the earliest it can come. */
  from: string;
  /** Its last local date (the top stair: the staircase's last day, or its first if it came later). */
  to: string;
  /** The rate has reached it by paceSteps' `date` (false for every row when no date is given). */
  reached: boolean;
}

/**
 * The whole step table on the course timetable: the first month (none when firstMonthDays is 0),
 * then each stair with its rate and dates (the flat pace: the first month and newPerDayAfter). For
 * the admin pace form and the walkthrough ("Mitä uutta"). `date` marks the rows reached by then and,
 * with the stairs reached (pace.rises), plans the stairs ahead from it.
 */
export function paceSteps(pace: PaceConfig, date?: DateInput): PaceStep[] {
  const t = date === undefined ? -Infinity : courseDay(date, pace);
  const rows: PaceStep[] = [];
  const dateOf = (d: number) => dateOfCourseDay(d, pace);
  const hold = pace.staircase?.hold ? holdEndDay(pace) : 0;
  if (hold > 0) {
    // the hold (one row, step -1 like the first month: it covers it) and what is left of the first month after it
    rows.push({ step: -1, rate: rateOnCourseDay(0, pace, null), from: dateOf(0), to: addDays(dateOf(hold), -1), reached: t >= 0 });
    if (pace.firstMonthDays > hold) {
      rows.push({ step: -1, rate: pace.newPerDayFirstMonth, from: dateOf(hold), to: addDays(dateOf(pace.firstMonthDays), -1), reached: t >= hold });
    }
  } else if (pace.firstMonthDays > 0) {
    rows.push({ step: -1, rate: pace.newPerDayFirstMonth, from: dateOf(0), to: addDays(dateOf(pace.firstMonthDays), -1), reached: t >= 0 });
  }
  const s = pace.staircase;
  if (!s) {
    const from = dateOf(pace.firstMonthDays);
    rows.push({ step: 0, rate: pace.newPerDayAfter, from, to: from, reached: t >= pace.firstMonthDays });
    return rows;
  }
  const reached = riseDays(pace);
  const starts = stairStarts(s, pace, reached, Number.isFinite(t) ? t : 0);
  const untilDay = daysBetween(pace.startDate, s.until);
  const current = Number.isFinite(t) && t >= stairBegin(pace) ? stairOn(t, s, pace, reached) : -1;
  starts.forEach((d, k) => {
    const last = k === starts.length - 1;
    const end = last ? Math.max(untilDay, d) : Math.max(d, starts[k + 1]! - 1);
    rows.push({ step: k, rate: stairRate(k, s), from: dateOf(d), to: dateOf(end), reached: k <= current });
  });
  return rows;
}

/** The next rise of the rate after `date` (nextRateRise). */
export interface RateRise {
  /** The stair it rises to (0 = the first stair, at the first month's end; never held). */
  step: number;
  /** New words a day from then. */
  rate: number;
  /** The local date it comes (planned: its calendar date, a week after the last rise at the earliest). */
  date: string;
  /**
   * Its date has come but it has not gone up: the server checks it on the day's first sync and holds
   * it while it is not going well (SharedState.riseHeld says why).
   */
  due: boolean;
}

/**
 * The next time the word rate goes up after `date`: null at the top stair, after the first month of
 * a flat pace, or when the rate would not rise. "6 uutta sanaa päivässä · seuraava nousu 25.12."
 */
export function nextRateRise(date: DateInput, pace: PaceConfig): RateRise | null {
  const t = courseDay(date, pace);
  const s = pace.staircase;
  const now = rateOnCourseDay(t, pace, riseDays(pace));
  if (s?.hold && t < holdEndDay(pace)) {
    // in the hold: the next rise is its end (to the first month's rate when it ends inside the first month)
    const end = holdEndDay(pace);
    const rate = rateOnCourseDay(end, pace, null);
    if (rate <= now) return null;
    return end < pace.firstMonthDays
      ? { step: -1, rate, date: dateOfCourseDay(end, pace), due: false }
      : { step: 0, rate, date: dateOfCourseDay(end, pace), due: false };
  }
  if (t < pace.firstMonthDays) {
    const rate = s ? stairRate(0, s) : pace.newPerDayAfter;
    if (rate <= Math.max(now, pace.newPerDayFirstMonth)) return null;
    return { step: 0, rate, date: dateOfCourseDay(stairBegin(pace), pace), due: false };
  }
  if (!s) return null;
  const reached = riseDays(pace);
  const cur = stairOn(t, s, pace, reached);
  if (cur + 1 >= stairCount(s) || s.step <= 0) return null;
  const day = stairStarts(s, pace, reached, t)[cur + 1]!;
  return { step: cur + 1, rate: stairRate(cur + 1, s), date: dateOfCourseDay(day, pace), due: day <= t };
}

/**
 * The reviews recall is measured on, as an SQL condition on review_log (api/src/frontier.ts recallCounts,
 * scripts/src/pace-recall.ts): daily or free study, no sentence cards, the card in FSRS's Review state
 * before the grade with a predicted recall in (0, 1] — the reviews pedagogy/logs.ts reviewSample keeps.
 * Recalled = any grade but Uudelleen (grade > 1).
 */
export const RECALL_REVIEW_SQL = `mode IN ('daily','free') AND type <> 'sentence' AND json_valid(log)
  AND json_extract(log, '$.state') = 2 AND json_extract(log, '$.r') > 0 AND json_extract(log, '$.r') <= 1`;

/** A user's recall over the window: review-state card reviews and how many were answered right. */
export interface RecallCount {
  reviews: number;
  recalled: number;
}

/** Why a due stair did not go up (riseGate): who is past the overdue pause, below the recall line, or has too few reviews. */
export interface RiseHold {
  reason: 'overdue' | 'recall' | 'unknown';
  users: string[];
}

/**
 * Is it going well enough for the next stair? null = yes: every user is within the overdue pause
 * (≤ pauseIfOverdueAbove) and has recalled at least staircase.minRecall of at least
 * STAIRCASE_RULES.minRecallReviews reviews. Otherwise the first reason that applies, with its users.
 */
export function riseGate(pace: PaceConfig, overdue: Record<string, number>, recall: Record<string, RecallCount>): RiseHold | null {
  const users = [...new Set([...Object.keys(overdue), ...Object.keys(recall)])].sort();
  const over = users.filter((u) => (overdue[u] ?? 0) > pace.pauseIfOverdueAbove);
  if (over.length) return { reason: 'overdue', users: over };
  const min = pace.staircase?.minRecall ?? 0;
  const low = users.filter((u) => {
    const r = recall[u];
    return !!r && r.reviews >= STAIRCASE_RULES.minRecallReviews && r.recalled / r.reviews < min;
  });
  if (low.length) return { reason: 'recall', users: low };
  const unknown = users.filter((u) => (recall[u]?.reviews ?? 0) < STAIRCASE_RULES.minRecallReviews);
  if (unknown.length) return { reason: 'unknown', users: unknown };
  return null;
}

export interface RiseCheck {
  /** The stairs reached after the check (the input's, plus today's rise if it went up). */
  rises: PaceRise[];
  /** Today's rise, if it went up. */
  rose: PaceRise | null;
  /** A due stair that did not go up, and why (null: nothing was due, or it went up). */
  hold: RiseHold | null;
}

/**
 * The daily check (api/src/frontier.ts ensureFrontier): when the next stair is due on `today`, it goes
 * up (one stair, never more) if riseGate says it is going well, else it holds. `pace.rises` = the
 * stairs reached so far (absent = none yet).
 */
export function checkRise(today: DateInput, pace: PaceConfig, overdue: Record<string, number>, recall: Record<string, RecallCount>): RiseCheck {
  const day = toLocalDate(today, pace.timezone);
  const rises = [...(pace.rises ?? [])];
  const next = pace.staircase ? nextRateRise(day, { ...pace, rises }) : null;
  if (!next || !next.due || next.step < 1) return { rises, rose: null, hold: null };
  const hold = riseGate(pace, overdue, recall);
  if (hold) return { rises, rose: null, hold };
  const rose = { step: next.step, date: day };
  return { rises: [...rises, rose], rose, hold: null };
}

/** The pace with the stairs reached (what the server attaches; SharedState.rises on the app). */
export function withRises(pace: PaceConfig, rises: readonly PaceRise[] | undefined): PaceConfig {
  if (!pace.staircase || rises === undefined) return pace;
  return { ...pace, rises: [...rises] };
}

// ---------- Opening early by a joint vote (Axel, 2026-10-07; docs/COURSE.md "Opening early") ----------
//
// When both of you have passed every open lesson and the next one still waits for its date, either
// may ask "Avataanko oppitunti N nyt?"; once both say yes it opens at once (api/src/routes/early-open.ts
// records it as an early_open row, which reaches the engine as pace.earlyOpens). From then on the
// timetable is anchored there: lesson N+1 opens exactly a week later, whatever the weekday, and
// every later lesson moves earlier by the same number of days (the course may end before Halloween
// 2027). Openings compound. The word rate and the release target count on the moved timetable
// (courseDay), and the early lesson's first week of words is released at once (earlyReleaseFloor).
// pace.startDate stays the Saturday it was: the shift is modelled beside it, never in it.

const lessonWeek = (order: number, pace: PaceConfig) => Math.floor((Math.max(1, order) - 1) / Math.max(1, pace.lessonsPerWeek));

/** Lesson `order`'s date under the openings `opens` (each anchors its week and every later one). */
function dateUnder(opens: readonly EarlyOpen[], order: number, pace: PaceConfig): string {
  const week = lessonWeek(order, pace);
  for (let i = opens.length - 1; i >= 0; i--) {
    const e = opens[i]!;
    const w = lessonWeek(e.lesson, pace);
    if (w <= week) return addDays(e.date, (week - w) * 7);
  }
  if (order <= 1 && pace.firstLessonDate && pace.firstLessonDate < pace.startDate) return pace.firstLessonDate;
  return addDays(pace.startDate, week * 7);
}

const opensCache = new WeakMap<readonly EarlyOpen[], { pace: string; opens: EarlyOpen[] }>();

/**
 * The early openings that count, in lesson order: the first lesson of a week after the first, each
 * strictly before the date the timetable gave it and not before the lesson before it (the vote only
 * ever records such a one; anything else — a stale row after the admin moved the start — is left out).
 */
export function earlyOpensOf(pace: PaceConfig): EarlyOpen[] {
  const raw = pace.earlyOpens;
  if (!raw?.length) return [];
  const key = `${pace.startDate}|${pace.lessonsPerWeek}|${pace.firstLessonDate ?? ''}`;
  const hit = opensCache.get(raw);
  if (hit && hit.pace === key) return hit.opens;
  const k = Math.max(1, pace.lessonsPerWeek);
  const sorted = raw
    .filter((e) => Number.isInteger(e.lesson) && e.lesson > k && (e.lesson - 1) % k === 0 && ISO_DATE.test(e.date))
    .sort((a, b) => a.lesson - b.lesson || a.date.localeCompare(b.date));
  const opens: EarlyOpen[] = [];
  for (const e of sorted) {
    if (opens.some((o) => o.lesson === e.lesson)) continue;
    const scheduled = dateUnder(opens, e.lesson, pace);
    const before = dateUnder(opens, e.lesson - 1, pace);
    if (e.date < scheduled && e.date >= before) opens.push({ lesson: e.lesson, date: e.date });
  }
  opensCache.set(raw, { pace: key, opens });
  return opens;
}

/** The opening that governs local date `date`: the last one on or before it (null = none yet). */
function openingAt(date: string, pace: PaceConfig): EarlyOpen | null {
  const opens = earlyOpensOf(pace);
  for (let i = opens.length - 1; i >= 0; i--) if (opens[i]!.date <= date) return opens[i]!;
  return null;
}

/**
 * Days the timetable has moved earlier by `date` (0 before any early opening): how much sooner
 * than its Saturday the governing opening came. Never decreases with the date.
 */
export function scheduleShift(date: DateInput, pace: PaceConfig): number {
  const e = openingAt(toLocalDate(date, pace.timezone), pace);
  return e ? daysBetween(e.date, addDays(pace.startDate, lessonWeek(e.lesson, pace) * 7)) : 0;
}

/** The weekday lessons open on as of `date` (0 = Sunday): LESSON_WEEKDAY until an early opening moves it. */
export function lessonWeekday(date: DateInput, pace: PaceConfig): number {
  const e = openingAt(toLocalDate(date, pace.timezone), pace);
  return e ? dayOfWeek(e.date) : LESSON_WEEKDAY;
}

/**
 * Words the frontier releases at once on `date` (0 = no early week): in an early-opened lesson's
 * first seven days, the release target through the end of that week (lesson 1's early opening by
 * pace.firstLessonDate: its first week, 7 × newPerDayFirstMonth).
 */
export function earlyReleaseFloor(date: DateInput, pace: PaceConfig): number {
  const day = toLocalDate(date, pace.timezone);
  const e = openingAt(day, pace);
  if (e) {
    const since = daysBetween(e.date, day);
    return since < 7 ? targetOnCourseDay(courseDay(e.date, pace) + 6, pace) : 0;
  }
  if (pace.firstLessonDate && day >= pace.firstLessonDate && day < pace.startDate) return 7 * pace.newPerDayFirstMonth;
  return 0;
}

export interface EarlyOpenOffer {
  /** The lesson that would open now (the next one). */
  lesson: number;
  /** The date it waits for. */
  date: string;
  /** Days sooner it would open, and every later lesson with it. */
  days: number;
}

/**
 * "Avataanko oppitunti N nyt?": offered only when both of you have passed every open lesson and the
 * next one is still waiting for its date (null otherwise: a lesson to pass first, its date has come
 * so it opens by itself, the course is all open, or it has not started).
 */
export function earlyOpenOffer(date: DateInput, pace: PaceConfig, bothPassedThrough: number, lessonCount: number): EarlyOpenOffer | null {
  const today = toLocalDate(date, pace.timezone);
  const open = lessonsOpen(today, pace, bothPassedThrough, lessonCount);
  if (open <= 0 || open >= lessonCount || Math.floor(bothPassedThrough) < open) return null;
  const lesson = open + 1;
  const at = unlockDateOf(lesson, pace);
  if (at <= today) return null;
  return { lesson, date: at, days: daysBetween(today, at) };
}

/** The pace with lesson `lesson` opened on `date` (what the server records once both said yes). */
export function withEarlyOpen(pace: PaceConfig, lesson: number, date: string): PaceConfig {
  return { ...pace, earlyOpens: [...(pace.earlyOpens ?? []), { lesson, date }] };
}

// ---------- Frontier ----------

export interface FrontierState {
  /** Count of course words released to both, in course order. */
  wordsReleased: number;
  /** Local date the frontier last advanced (null = never). */
  lastAdvanced: string | null;
}

export interface FrontierInputs {
  pace: PaceConfig;
  /** Overdue review count per user id. */
  overdue: Record<string, number>;
  /** Number of course words belonging to open lessons (the frontier never passes it). */
  releasableWords: number;
  /**
   * The calendar's target for today (releaseTarget). Behind it, a day releases up to twice its
   * rate until caught up (a late lesson, a paused week); omitted = no catch-up.
   */
  target?: number;
}

export interface FrontierResult {
  state: FrontierState;
  /** Words released by this call (0 when already advanced today, paused, or capped). */
  released: number;
  paused: boolean;
  pausedReason?: 'overdue';
  /** Users whose overdue count exceeded the threshold. */
  overdueUsers: string[];
  newPerDay: number;
}

/**
 * Advance the shared frontier for local date `today`. Idempotent per day: a second call on the
 * same day returns the same state with `released = 0`. A paused day does not count as advanced,
 * so clearing the overdue backlog later the same day lets the frontier advance.
 */
export function advanceFrontier(state: FrontierState, today: DateInput, inputs: FrontierInputs): FrontierResult {
  const { pace } = inputs;
  const day = toLocalDate(today, pace.timezone);
  const perDay = newPerDay(day, pace);
  const overdueUsers = Object.entries(inputs.overdue)
    .filter(([, n]) => n > pace.pauseIfOverdueAbove)
    .map(([u]) => u)
    .sort();
  const paused = overdueUsers.length > 0;
  const base: Omit<FrontierResult, 'state' | 'released'> = {
    paused,
    overdueUsers,
    newPerDay: perDay,
    ...(paused ? { pausedReason: 'overdue' as const } : {}),
  };
  const cap = Math.max(state.wordsReleased, inputs.releasableWords);
  // A lesson opened early (lesson 1 by pace.firstLessonDate, Axel 2026-10-06; any later one by the
  // joint vote, 2026-10-07): its whole first week of words is released at once — also later on a
  // day the frontier already advanced, since the vote can open it in the middle of the day. The cap
  // (words of open lessons) keeps the rest of the timetable: nothing more until the next lesson.
  // in the hold (Axel 2026-10-25) an early opening releases no week at once and nothing catches up: perDay is the cap
  const holding = inPaceHold(day, pace);
  const floor = paused || holding ? 0 : earlyReleaseFloor(day, pace);
  if (floor > state.wordsReleased && cap > state.wordsReleased) {
    const early = Math.min(cap, floor);
    return { ...base, state: { wordsReleased: early, lastAdvanced: day }, released: early - state.wordsReleased };
  }
  if (state.lastAdvanced !== null && daysBetween(state.lastAdvanced, day) <= 0) {
    return { ...base, state: { ...state }, released: 0 };
  }
  if (floor > 0 && perDay === 0) return { ...base, state: { wordsReleased: state.wordsReleased, lastAdvanced: day }, released: 0 };
  if (paused || perDay === 0) return { ...base, state: { ...state }, released: 0 };
  const behind = Math.max(0, (inputs.target ?? 0) - state.wordsReleased);
  const step = holding ? perDay : Math.max(perDay, Math.min(2 * perDay, behind));
  const next = Math.min(cap, state.wordsReleased + step);
  return {
    ...base,
    state: { wordsReleased: next, lastAdvanced: day },
    released: next - state.wordsReleased,
  };
}

// ---------- Course word list ----------

export interface CourseWord {
  lemmaId: string;
  lessonId: string;
  lessonOrder: number;
}

/** Course words in release order: lessons by `order`, each lesson's lemmaIds in order, first occurrence wins. */
export function courseWordList(lessons: readonly LessonMeta[]): CourseWord[] {
  const seen = new Set<string>();
  const out: CourseWord[] = [];
  for (const l of [...lessons].sort((a, b) => a.order - b.order)) {
    for (const id of l.lemmaIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ lemmaId: id, lessonId: l.id, lessonOrder: l.order });
    }
  }
  return out;
}

/** Number of course words that belong to lessons with order ≤ `lessonsUnlocked` (the open lessons). */
export function releasableWordCount(words: readonly CourseWord[], unlocked: number): number {
  return words.filter((w) => w.lessonOrder <= unlocked).length;
}

/** The released prefix of the course word list. */
export function releasedWords(words: readonly CourseWord[], wordsReleased: number): CourseWord[] {
  return words.slice(0, Math.max(0, wordsReleased));
}

/**
 * Released course words this user may study as new cards: those whose lesson the user has
 * passed. Order is preserved.
 */
export function wordsAvailableForUser(released: readonly CourseWord[], passedLessonIds: Iterable<string>): string[] {
  const passed = new Set(passedLessonIds);
  return released.filter((w) => passed.has(w.lessonId)).map((w) => w.lemmaId);
}
